import { useCallback, useState } from 'react'

import { mutate } from 'swr'

import { trackAction, trackSlotSaved } from '@features/my-gousto-page'
import type { OrderCardActionState, SaveSlotSelection } from '@features/my-gousto-page'
import { cancelOrder, editOrderDelivery } from '@library/api-customer-engagement-experience'
import type { DashboardAnalytics } from '@library/api-customer-engagement-experience'

import { UNSUBSCRIBED_DASHBOARD_SWR_KEY } from './useUnsubscribedDashboard'

const IDLE_STATE: OrderCardActionState = {
  isSaveSlotPending: false,
  hasSaveSlotError: false,
  isSkipDeliveryPending: false,
  hasSkipDeliveryError: false,
}

type MutationFlags = {
  pending: keyof OrderCardActionState
  error: keyof OrderCardActionState
}

type UseUnsubscribedDashboardOrderActionsResult = {
  saveSlot: (
    orderId: string,
    selection: SaveSlotSelection,
    analytics?: DashboardAnalytics,
  ) => Promise<void>
  skipDelivery: (orderId: string, analytics?: DashboardAnalytics) => Promise<void>
  getOrderState: (orderId: string) => OrderCardActionState
}

/**
 * Owns the per-order pending/error state for the editable upcoming-delivery actions
 * (change slot, skip delivery) and revalidates the dashboard once a mutation succeeds
 * so the order section re-renders with fresh data.
 */
export function useUnsubscribedDashboardOrderActions(): UseUnsubscribedDashboardOrderActionsResult {
  const [states, setStates] = useState<Record<string, OrderCardActionState>>({})

  const patch = useCallback((orderId: string, changes: Partial<OrderCardActionState>) => {
    setStates((prev) => ({
      ...prev,
      [orderId]: { ...IDLE_STATE, ...prev[orderId], ...changes },
    }))
  }, [])

  // Both delivery mutations share the same flow — flag pending, run the request, then
  // on success revalidate the dashboard — and differ only in which flag pair they toggle.
  const runMutation = useCallback(
    async (
      orderId: string,
      flags: MutationFlags,
      request: () => Promise<void>,
      onSuccess?: () => void,
    ) => {
      patch(orderId, { [flags.pending]: true, [flags.error]: false })

      try {
        await request()
      } catch {
        patch(orderId, { [flags.pending]: false, [flags.error]: true })

        return
      }

      await mutate(UNSUBSCRIBED_DASHBOARD_SWR_KEY)
      patch(orderId, { [flags.pending]: false })
      onSuccess?.()
    },
    [patch],
  )

  const saveSlot = useCallback(
    (
      orderId: string,
      {
        delivery_day_id: deliveryDayId,
        delivery_slot_id: deliverySlotId,
        day_slot_lead_time_id: daySlotLeadTimeId,
      }: SaveSlotSelection,
      analytics?: DashboardAnalytics,
    ) =>
      runMutation(
        orderId,
        { pending: 'isSaveSlotPending', error: 'hasSaveSlotError' },
        () => editOrderDelivery(orderId, { deliveryDayId, deliverySlotId, daySlotLeadTimeId }),
        () => trackSlotSaved(analytics, { newSlotId: deliverySlotId }),
      ),
    [runMutation],
  )

  const skipDelivery = useCallback(
    (orderId: string, analytics?: DashboardAnalytics) =>
      runMutation(
        orderId,
        { pending: 'isSkipDeliveryPending', error: 'hasSkipDeliveryError' },
        () => cancelOrder(orderId),
        () => trackAction(analytics),
      ),
    [runMutation],
  )

  const getOrderState = useCallback(
    (orderId: string): OrderCardActionState => states[orderId] ?? IDLE_STATE,
    [states],
  )

  return { saveSlot, skipDelivery, getOrderState }
}
